// 005 (FR-030..FR-033): the server's cart, in memory only. No runtime imports: the session
// provider clears it at sign-out, and importing the API client here would make an import cycle.
// Every change is a server call and only the server's answer is shown. At most one request per line is in flight; taps while it runs only move
// the intended quantity, which is sent when the request returns (FR-031). Errors go to listeners.
import type { AppError } from "@/api/client";
import type { Api } from "@/api/types";

import type { ShopClient } from "./shopApi";

type Cart = Api["Cart"];
type LineKey = `${string}:${string}`;

export type CartState = {
  cart: Cart | null;
  /** Lines with a request in flight. */
  pending: ReadonlySet<LineKey>;
  /** The last refusal, with the line it was for. */
  error: { key: LineKey | "fullSet" | "load"; error: AppError } | null;
};

export const lineKey = (studentId: string, productId: string): LineKey =>
  `${studentId}:${productId}`;

export function createCartStore() {
  let state: CartState = { cart: null, pending: new Set(), error: null };
  const listeners = new Set<() => void>();
  const wanted = new Map<LineKey, number>();
  const set = (next: Partial<CartState>) => {
    state = { ...state, ...next };
    listeners.forEach((l) => l());
  };

  async function send(
    client: ShopClient,
    studentId: string,
    productId: string,
  ) {
    const key = lineKey(studentId, productId);
    const quantity = wanted.get(key)!;
    try {
      const cart = await client.setLine({ studentId, productId, quantity });
      set({ cart });
    } catch (e) {
      wanted.delete(key);
      set({ error: { key, error: e as AppError } });
    }
    if (wanted.has(key) && wanted.get(key) !== quantity) {
      await send(client, studentId, productId);
      return;
    }
    wanted.delete(key);
    const pending = new Set(state.pending);
    pending.delete(key);
    set({ pending });
  }

  return {
    getSnapshot: () => state,
    subscribe(l: () => void) {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    async load(client: ShopClient) {
      try {
        set({ cart: await client.getCart() });
      } catch (e) {
        set({ error: { key: "load", error: e as AppError } });
      }
    },
    /** Asks the server for `quantity` (0 removes the line). */
    setQuantity(
      client: ShopClient,
      studentId: string,
      productId: string,
      quantity: number,
    ) {
      const key = lineKey(studentId, productId);
      const busy = state.pending.has(key);
      wanted.set(key, Math.max(0, Math.min(20, quantity)));
      if (busy) return;
      set({ pending: new Set(state.pending).add(key), error: null });
      void send(client, studentId, productId);
    },
    async addFullSet(
      client: ShopClient,
      studentId: string,
    ): Promise<
      | { ok: true; added: number; raised: number }
      | { ok: false; error: AppError }
    > {
      set({ error: null });
      try {
        const result = await client.addFullSet(studentId);
        set({ cart: result.cart });
        return { ok: true, added: result.added, raised: result.raised };
      } catch (e) {
        return { ok: false, error: e as AppError };
      }
    },
    clearError: () => set({ error: null }),
    clear() {
      wanted.clear();
      state = { cart: null, pending: new Set(), error: null };
      listeners.forEach((l) => l());
    },
  };
}

/** The parent's cart (one per app). On-behalf carts (010) create their own store. */
export const parentCart = createCartStore();
export type CartStore = ReturnType<typeof createCartStore>;

/** The server's quantity of a product for a student (0 when not in the cart). */
export function quantityIn(
  cart: Cart | null,
  studentId: string,
  productId: string,
): number {
  return (
    cart?.children
      .find((c) => c.studentId === studentId)
      ?.lines.find((l) => l.productId === productId)?.quantity ?? 0
  );
}
